import React, { useState, useEffect } from "react";
import { Box, Typography, Divider, Switch, IconButton } from "@mui/material";
import { campaignManagementService } from "services/campaign";
import { toast } from "react-toastify";
import { OverlayLoader } from "components/Common/Loader/loaderWithOverlay";
import CodeIcon from "@mui/icons-material/Code";

interface CampaignHeaderProps {
  campaignId: string;
  campaignName: string;
  totalLeads: number;
  acceptanceRate: number;
  replyRate: number;
  createdAt: string;
  campaignStatus: boolean;
  expertId: string;
  showSidebarToggle?: boolean;
  sidebarOpen?: boolean;
  onSidebarToggle?: () => void;
  onStatusChange?: (params: {
    newStatus?: boolean;
    autoSource?: boolean;
  }) => void;
  autoSource?: boolean;
}

const CampaignHeader: React.FC<CampaignHeaderProps> = ({
  campaignId,
  campaignName,
  totalLeads,
  acceptanceRate,
  replyRate,
  createdAt,
  campaignStatus,
  expertId,
  showSidebarToggle = false,
  sidebarOpen = false,
  onSidebarToggle,
  onStatusChange,
  autoSource = false,
}) => {
  const [campaignStatusToggle, setCampaignStatusToggle] =
    useState<boolean>(campaignStatus);
  const [localLoader, setLocalLoader] = useState<boolean>(false);
  const [autoSourceToggle, setAutoSourceToggle] = useState<boolean>(autoSource);

  useEffect(() => {
    setCampaignStatusToggle(campaignStatus);
  }, [campaignStatus]);

  useEffect(() => {
    setAutoSourceToggle(autoSource);
  }, [autoSource]);

  const handleToggle = async (
    type: "status" | "autoSource",
    value: boolean
  ) => {
    setLocalLoader(true);

    try {
      const payload =
        type === "status"
          ? { status: value ? "ACTIVE" : "NOT_ACTIVE" }
          : { autoSource: value ? "ACTIVE" : "NOT_ACTIVE" };

      const response = await campaignManagementService.updateCampaignStatus(
        expertId,
        campaignId,
        payload.status,
        payload.autoSource
      );

      if (response?.apiStatus === "SUCCESS") {
        if (type === "status") {
          setCampaignStatusToggle(value);
          toast.success("Campaign status updated successfully");
        } else {
          setAutoSourceToggle(value);
          toast.success("Campaign auto source updated successfully");
        }
      } else {
        console.error(
          `Failed to update campaign ${type}`,
          response?.statusText || "Unknown error"
        );
        toast.error(`Failed to update campaign ${type}`);
      }
    } catch (error: any) {
      console.error(error.message);
      toast.error(`Failed to update campaign ${type}`);
    } finally {
      setLocalLoader(false);
    }
  };

  const handleToggleCampaignStatus = (e: any) =>
    handleToggle("status", e.target.checked);

  const handleToggleAutoSource = (e: any) =>
    handleToggle("autoSource", e.target.checked);

  return (
    <Box
      sx={{
        display: "flex",
        padding: "16px 30px",
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        alignItems: "center",
        mb: 2,
        width: "100%",
        background: "#fff",
        position: "relative",
        transition: "margin-left 0.3s",
        justifyContent: "space-between",
        flexWrap: "wrap",
      }}
    >
      <OverlayLoader loading={localLoader} />

      {showSidebarToggle && (
        <IconButton
          onClick={onSidebarToggle}
          sx={{
            position: "absolute",
            left: "-15px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1200,
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            width: "32px",
            height: "32px",
            minWidth: "32px",
          }}
        >
          <CodeIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      )}

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            mr: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="div"
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#5175e0",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 16.5C21 16.88 20.79 17.21 20.47 17.38L12.57 21.82C12.41 21.94 12.21 22 12 22C11.79 22 11.59 21.94 11.43 21.82L3.53 17.38C3.21 17.21 3 16.88 3 16.5V7.5C3 7.12 3.21 6.79 3.53 6.62L11.43 2.18C11.59 2.06 11.79 2 12 2C12.21 2 12.41 2.06 12.57 2.18L20.47 6.62C20.79 6.79 21 7.12 21 7.5V16.5Z"
                fill="white"
              />
            </svg>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#333",
            }}
          >
            {campaignName || "loading campaign..."}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                bgcolor: "#e0e6ff",
                height: 4,
                width: 138,
                borderRadius: 2,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#5175e0",
                  height: 4,
                  width: "60%",
                  borderRadius: 2,
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "space-evenly",
          flexGrow: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", textAlign: "left" }}>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mr: 3, borderColor: "#e0e0e0" }}
          />
          <Box>
            <Typography
              variant="h6"
              fontWeight="medium"
              sx={{
                textAlign: "left",
                fontSize: "1.1rem",
                fontWeight: "600",
              }}
            >
              {totalLeads}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: "left",
                fontSize: "0.75rem",
                fontWeight: 400,
              }}
            >
              Total Leads
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", textAlign: "left" }}>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mr: 3, borderColor: "#e0e0e0" }}
          />
          <Box>
            <Typography
              variant="h6"
              fontWeight="medium"
              sx={{
                textAlign: "left",
                fontSize: "1.1rem",
                fontWeight: "600",
              }}
            >
              {acceptanceRate}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: "left",
                fontSize: "0.75rem",
                fontWeight: 400,
              }}
            >
              Acceptance Rate
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", textAlign: "left" }}>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mr: 3, borderColor: "#e0e0e0" }}
          />
          <Box>
            <Typography
              variant="h6"
              fontWeight="medium"
              sx={{
                textAlign: "left",
                fontSize: "1.1rem",
                fontWeight: "600",
              }}
            >
              {replyRate}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: "left",
                fontSize: "0.75rem",
                fontWeight: 400,
              }}
            >
              Reply rate
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "left" }}>
          <Typography
            variant="h6"
            fontWeight="medium"
            sx={{
              textAlign: "left",
              fontSize: "1.1rem",
              fontWeight: "600",
            }}
          >
            {createdAt}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "left",
              fontSize: "0.75rem",
              fontWeight: 400,
            }}
          >
            Created
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            textAlign: "left",
          }}
        >
          <Switch
            checked={campaignStatusToggle}
            onChange={handleToggleCampaignStatus}
            size="small"
            sx={{
              "& .MuiSwitch-thumb": {
                backgroundColor: campaignStatusToggle ? "#325CD6" : "#FAFAFA",
              },
              "& .MuiSwitch-track": {
                backgroundColor: campaignStatusToggle ? "#90caf9" : "#DFE2E7",
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
              color: "#5A5E67",
              textAlign: "left",
            }}
          >
            {campaignStatusToggle ? "Active" : "Not Active"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            textAlign: "left",
          }}
        >
          <Switch
            checked={autoSourceToggle}
            onChange={handleToggleAutoSource}
            size="small"
            sx={{
              "& .MuiSwitch-thumb": {
                backgroundColor: autoSourceToggle ? "#325CD6" : "#FAFAFA",
              },
              "& .MuiSwitch-track": {
                backgroundColor: autoSourceToggle ? "#90caf9" : "#DFE2E7",
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
              color: "#5A5E67",
              textAlign: "left",
            }}
          >
           Auto Source
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignHeader;
